import dayjs from 'dayjs';
import { Banknote, CreditCard, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useDeleteTransfer } from '@/api/transfer/use-delete-transfer.ts';
import { type TransferDto, TransferDtoMethodEnum, TransferDtoTypeEnum } from '@/api/types';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { formatPrice } from '@/helpers/formatters.ts';
import { cn } from '@/lib/utils';

type TransferRowProps = {
  transfer: TransferDto;
};

const TransferRow = ({ transfer }: TransferRowProps) => {
  const navigate = useNavigate();

  const { mutate: deleteTransfer, isPending } = useDeleteTransfer();

  const isCash = transfer.method === TransferDtoMethodEnum.Cash;
  const isDebit = transfer.type === TransferDtoTypeEnum.Debit;

  const categories = [
    ...new Map(
      transfer.records
        .filter((record) => record.category)
        .map((record) => [record.category!.uuid, record.category!]),
    ).values(),
  ];

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (confirm('Delete this transfer? This can only be undone in the database.')) {
      deleteTransfer(transfer.uuid);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 border rounded-lg transition-colors text-xs font-normal cursor-pointer',
        isDebit
          ? 'bg-red-50/60 hover:bg-red-50 border-red-200 dark:bg-red-950/10 dark:hover:bg-red-950/20 dark:border-red-900'
          : 'bg-green-50/60 hover:bg-green-50 border-green-200 dark:bg-green-950/10 dark:hover:bg-green-950/20 dark:border-green-900',
      )}
      onClick={() => navigate(transfer.uuid)}
    >
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            isCash ? 'bg-orange-100 dark:bg-orange-950' : 'bg-sky-100 dark:bg-sky-950',
          )}
        >
          {isCash ? (
            <Banknote className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          ) : (
            <CreditCard className="w-5 h-5 text-sky-600 dark:text-sky-400" />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="font-medium text-sm">{transfer.party || transfer.rawParty}</span>
            {categories.map((category) => (
              <Badge key={category.uuid} variant="info">
                {category.name}
              </Badge>
            ))}
          </div>
          <span className="text-muted-foreground">{dayjs(transfer.date).format('DD.MM.YYYY')}</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'font-semibold text-lg',
            isDebit ? 'text-red-600 dark:text-red-500' : 'text-green-600 dark:text-green-500',
          )}
        >
          {formatPrice(isDebit ? -transfer.amount : transfer.amount)}
        </span>
        <Button variant="outline" size="icon" disabled={isPending} onClick={handleDelete}>
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default TransferRow;
